"use client";

/* ==========================================================================
   Body scroll lock — single owner, reference counted
   --------------------------------------------------------------------------
   Every overlay that covers the page has to stop the page scrolling behind it.
   The naive way — each component saving `document.body.style.overflow` on open
   and writing it back on close — is broken the moment two overlays overlap,
   because the second one snapshots the FIRST one's lock as if it were the
   page's normal state and then restores it forever.

   That is exactly the bug this module was written to kill: the mobile search
   sheet locked the body, the lead popup opened 5s later behind it and recorded
   "overflow was hidden", the sheet closed and restored correctly, then the lead
   popup unmounted on the /search navigation and wrote `hidden` back. /search
   rendered fine and could not be scrolled.

   So there is now exactly ONE writer of the body's scroll styles in the app,
   and it counts holders:

     const release = lockScroll();   // first caller snapshots + applies
     release();                      // last caller restores; the rest are no-ops

   Prefer the `useScrollLock(enabled)` hook (hooks/useScrollLock.js) in React —
   it is the same thing wired to an effect's lifecycle.

   Notes on the technique itself:

   * iOS Safari ignores `overflow: hidden` on <body>. The only reliable lock
     there is pinning the body with `position: fixed; top: -scrollY`, then
     scrolling back on release. That pin is visually disruptive (it snaps the
     page to 0 and back) and it is NOT needed anywhere else, so it is applied
     only on iOS. Every other browser gets plain `overflow: hidden`, which
     leaves the page exactly where it was.

   * Removing the scrollbar reflows a desktop page by ~15px. `paddingRight`
     compensation holds the layout still.

   * `overscroll-behavior: none` stops Android pull-to-refresh and scroll
     chaining from firing while a modal is up.

   * The scroll position is restored ONLY if the URL has not changed since the
     lock was taken. A release can land after a client-side navigation (the
     lead-popup unmount above is precisely that), and scrolling the freshly
     opened page to the previous page's offset is its own bug.

   * `html[data-scroll-locked]` is exported to CSS so floating chrome that lives
     in the root stacking context (the WhatsApp FAB at z-999, SmartAppBanner at
     z-99999) can be suppressed for ANY modal — see globals.css.
   ========================================================================== */

let lockCount = 0;
let snapshot = null;
let lockedScrollY = 0;
let lockedUrl = "";

const listeners = new Set();

// iPadOS reports itself as "Macintosh"; the touch-point count is what separates
// it from a real Mac.
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iP(hone|od|ad)/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
};

const currentUrl = () => `${window.location.pathname}${window.location.search}`;

const notify = () => {
  const locked = lockCount > 0;
  listeners.forEach((fn) => {
    try {
      fn(locked);
    } catch {
      /* a subscriber blowing up must not leave the lock half-applied */
    }
  });
};

const apply = () => {
  const body = document.body;
  const html = document.documentElement;

  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  lockedUrl = currentUrl();

  snapshot = {
    position: body.style.position,
    top: body.style.top,
    left: body.style.left,
    right: body.style.right,
    width: body.style.width,
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
    overscrollBehavior: html.style.overscrollBehavior,
    pinned: isIOS(),
  };

  const scrollbarWidth = window.innerWidth - html.clientWidth;
  if (scrollbarWidth > 0) {
    const existing = parseFloat(window.getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${existing + scrollbarWidth}px`;
  }

  html.style.overscrollBehavior = "none";
  body.style.overflow = "hidden";

  if (snapshot.pinned) {
    body.style.position = "fixed";
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
  }

  html.dataset.scrollLocked = "1";
};

const restore = () => {
  const taken = snapshot;
  snapshot = null;
  if (!taken) return;

  const body = document.body;
  const html = document.documentElement;

  body.style.position = taken.position;
  body.style.top = taken.top;
  body.style.left = taken.left;
  body.style.right = taken.right;
  body.style.width = taken.width;
  body.style.overflow = taken.overflow;
  body.style.paddingRight = taken.paddingRight;
  html.style.overscrollBehavior = taken.overscrollBehavior;
  delete html.dataset.scrollLocked;

  // Only the iOS pin actually moved the page, and only put it back if we are
  // still on the page we moved.
  if (taken.pinned && currentUrl() === lockedUrl) {
    window.scrollTo(0, lockedScrollY);
  }
};

/**
 * Take a scroll lock. Returns the release function — idempotent, so calling it
 * twice (React StrictMode, a double close) cannot drive the count negative.
 */
export const lockScroll = () => {
  if (typeof document === "undefined") return () => {};

  if (lockCount === 0) apply();
  lockCount += 1;
  notify();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) restore();
    notify();
  };
};

/** True while any overlay holds the lock. Use it to avoid stacking modals. */
export const isScrollLocked = () => lockCount > 0;

/**
 * Subscribe to lock/unlock. Returns an unsubscribe function. Shaped to be
 * handed straight to useSyncExternalStore — see hooks/useScrollLock.js.
 */
export const subscribeScrollLock = (fn) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};
